import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { LogIn, LogOut, Users, Eye, Clock, FileText, RefreshCw, Trash2, Plus, Loader2, Mail, TrendingUp, ShieldCheck, BookOpen, Sparkles, ScrollText, Eye as EyeIcon, EyeOff, Power, MessageSquare, ExternalLink, Save, Scale, CalendarClock, Monitor, Smartphone, Film, Wand2, Gauge, ShieldAlert, Ban } from "lucide-react";
import logo from "@/assets/smart-logo.webp";
import { OCCASIONS, OCCASION_COUNT } from "@/occasion/registry";
import { detectOccasion, occasionById, formatHijri } from "@/occasion/engine";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "sav_admin_token";
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY) || ""}` } });

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="rounded-2xl border border-line bg-white p-5 shadow-card">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 grid place-items-center rounded-xl bg-navy/5 text-navy"><Icon className="h-5 w-5" /></div>
        <div>
          <div className="text-2xl font-display text-navy leading-none">{value}</div>
          <div className="text-xs text-muted mt-1">{label}</div>
        </div>
      </div>
      {sub && <div className="text-[0.7rem] text-muted mt-2">{sub}</div>}
    </div>
  );
}

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      const res = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      onLogin();
    } catch (e) { setErr("Invalid email or password"); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-surface px-4">
      <form onSubmit={submit} className="w-full max-w-sm rounded-2xl border border-line bg-white shadow-lift p-8" data-testid="admin-login-form">
        <img src={logo} alt="Smart Account Vision" className="h-14 mx-auto" />
        <h1 className="font-display text-2xl text-navy text-center mt-4">Admin Login</h1>
        <p className="text-center text-sm text-muted mt-1">Smart Account Vision control panel</p>
        <div className="mt-6 space-y-3">
          <input type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email" className="w-full h-11 px-4 rounded-lg border border-line focus:outline-none focus:border-navy" dir="ltr" />
          <input type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password" className="w-full h-11 px-4 rounded-lg border border-line focus:outline-none focus:border-navy" dir="ltr" />
        </div>
        {err && <p className="text-crimson text-sm mt-3" data-testid="admin-login-error">{err}</p>}
        <button disabled={loading} data-testid="admin-login-submit" className="mt-5 w-full h-11 rounded-full bg-navy text-white font-semibold inline-flex items-center justify-center gap-2 hover:bg-navy-600 transition-colors disabled:opacity-60">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />} Sign in
        </button>
      </form>
    </div>
  );
}

const emptyUpdate = { title_en: "", title_ar: "", summary_en: "", summary_ar: "", category: "Update", source_url: "", date: "", published: true };
const emptyArticle = { id: "", slug: "", service: "", title_en: "", title_ar: "", excerpt_en: "", excerpt_ar: "", body_en: "", body_ar: "", source_name: "", source_url: "", official: false, published: true, date: "" };

/* ---------------- Overview + Tax Updates (existing) ---------------- */
function OverviewTab({ onLogout }) {
  const [analytics, setAnalytics] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [fetchMsg, setFetchMsg] = useState("");
  const [form, setForm] = useState(emptyUpdate);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [a, u, c] = await Promise.all([
        axios.get(`${API}/admin/analytics`, authHeaders()),
        axios.get(`${API}/admin/tax-updates`, authHeaders()),
        axios.get(`${API}/admin/contacts`, authHeaders()),
      ]);
      setAnalytics(a.data); setUpdates(u.data); setContacts(c.data);
    } catch (e) { if (e?.response?.status === 401) onLogout(); }
  }, [onLogout]);
  useEffect(() => { load(); }, [load]);

  const fetchFTA = async () => {
    setFetching(true); setFetchMsg("");
    try {
      const res = await axios.post(`${API}/admin/tax-updates/fetch`, {}, authHeaders());
      setFetchMsg(`${res.data.added} new update(s) added. ${res.data.note || ""}`);
      await load();
    } catch (e) { setFetchMsg("Fetch failed. Please try again."); }
    finally { setFetching(false); }
  };
  const addUpdate = async (e) => {
    e.preventDefault();
    if (!form.title_en && !form.title_ar) return;
    setSaving(true);
    try { await axios.post(`${API}/admin/tax-updates`, form, authHeaders()); setForm(emptyUpdate); await load(); }
    finally { setSaving(false); }
  };
  const delUpdate = async (id) => { await axios.delete(`${API}/admin/tax-updates/${id}`, authHeaders()); await load(); };

  const maxDay = Math.max(1, ...(analytics?.daily || []).map((d) => d.views));
  const fmtTime = (s) => s >= 60 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${s}s`;

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Unique visitors" value={analytics?.total_visitors ?? "—"} />
        <StatCard icon={Eye} label="Page views" value={analytics?.total_views ?? "—"} />
        <StatCard icon={Clock} label="Avg. time on site" value={analytics ? fmtTime(analytics.avg_time_seconds) : "—"} />
        <StatCard icon={Mail} label="Consultation requests" value={analytics?.contacts_count ?? "—"} />
      </section>

      <div className="grid lg:grid-cols-3 gap-6">
        <section className="lg:col-span-2 rounded-2xl border border-line bg-white p-6 shadow-card">
          <div className="flex items-center gap-2 mb-4"><TrendingUp className="h-4 w-4 text-navy" /><h2 className="font-display text-lg text-navy">Traffic — last 14 days</h2></div>
          <div className="flex items-end gap-1.5 h-40">
            {(analytics?.daily || []).map((d, i) => (
              <div key={i} className="flex-1 flex flex-col items-center justify-end group">
                <div className="w-full rounded-t bg-navy/80 hover:bg-crimson transition-colors" style={{ height: `${(d.views / maxDay) * 100}%`, minHeight: d.views ? 4 : 0 }} title={`${d.date}: ${d.views}`} />
              </div>
            ))}
          </div>
          <div className="flex justify-between text-[0.65rem] text-muted mt-2"><span>{analytics?.daily?.[0]?.date}</span><span>{analytics?.daily?.[analytics.daily.length - 1]?.date}</span></div>
        </section>
        <section className="rounded-2xl border border-line bg-white p-6 shadow-card">
          <h2 className="font-display text-lg text-navy mb-4">Top pages</h2>
          <ul className="space-y-2">
            {(analytics?.top_pages || []).map((p, i) => (
              <li key={i} className="flex items-center justify-between text-sm"><span className="text-ink/80 truncate" dir="ltr">{p.path}</span><span className="font-semibold text-navy">{p.views}</span></li>
            ))}
            {!analytics?.top_pages?.length && <li className="text-sm text-muted">No data yet.</li>}
          </ul>
        </section>
      </div>

      <section className="rounded-2xl border border-line bg-white p-6 shadow-card">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div className="flex items-center gap-2"><FileText className="h-4 w-4 text-navy" /><h2 className="font-display text-lg text-navy">FTA / Tax updates ({updates.length})</h2></div>
          <button onClick={fetchFTA} disabled={fetching} data-testid="fetch-fta" className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-crimson text-white font-semibold text-sm hover:bg-crimson-600 transition-colors disabled:opacity-60">
            {fetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Fetch latest FTA updates
          </button>
        </div>
        {fetchMsg && <p className="text-sm text-brandgreen mb-4" data-testid="fetch-msg">{fetchMsg}</p>}
        <form onSubmit={addUpdate} className="grid sm:grid-cols-2 gap-3 mb-6 rounded-xl bg-surface p-4">
          <input placeholder="Title (English)" value={form.title_en} onChange={(e) => setForm({ ...form, title_en: e.target.value })} data-testid="update-title-en" className="h-10 px-3 rounded-lg border border-line" dir="ltr" />
          <input placeholder="العنوان (عربي)" value={form.title_ar} onChange={(e) => setForm({ ...form, title_ar: e.target.value })} data-testid="update-title-ar" className="h-10 px-3 rounded-lg border border-line" dir="rtl" />
          <input placeholder="Summary (English)" value={form.summary_en} onChange={(e) => setForm({ ...form, summary_en: e.target.value })} className="h-10 px-3 rounded-lg border border-line" dir="ltr" />
          <input placeholder="الملخص (عربي)" value={form.summary_ar} onChange={(e) => setForm({ ...form, summary_ar: e.target.value })} className="h-10 px-3 rounded-lg border border-line" dir="rtl" />
          <input placeholder="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="h-10 px-3 rounded-lg border border-line" dir="ltr" />
          <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="h-10 px-3 rounded-lg border border-line" dir="ltr" />
          <input placeholder="Source URL (optional)" value={form.source_url} onChange={(e) => setForm({ ...form, source_url: e.target.value })} className="h-10 px-3 rounded-lg border border-line sm:col-span-2" dir="ltr" />
          <button disabled={saving} data-testid="add-update" className="sm:col-span-2 h-10 rounded-full bg-navy text-white font-semibold inline-flex items-center justify-center gap-2 hover:bg-navy-600 transition-colors disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add update
          </button>
        </form>
        <div className="divide-y divide-line">
          {updates.map((u) => (
            <div key={u.id} className="py-3 flex items-start justify-between gap-3" data-testid="update-row">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-[0.65rem] font-bold uppercase bg-navy/5 text-navy px-2 py-0.5 rounded">{u.category}</span>
                  {u.origin === "fetched" && <span className="text-[0.6rem] bg-crimson-soft text-crimson-600 px-2 py-0.5 rounded">auto</span>}
                  <span className="text-xs text-muted" dir="ltr">{u.date}</span>
                </div>
                <div className="text-ink font-medium mt-1" dir="ltr">{u.title_en || u.title_ar}</div>
              </div>
              <button onClick={() => delUpdate(u.id)} className="text-crimson hover:bg-crimson hover:text-white p-2 rounded-lg transition-colors shrink-0" aria-label="delete"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-2xl border border-line bg-white p-6 shadow-card">
        <h2 className="font-display text-lg text-navy mb-4">Recent consultation requests</h2>
        <div className="divide-y divide-line">
          {contacts.slice(0, 10).map((c) => (
            <div key={c.id} className="py-3 flex flex-wrap items-center justify-between gap-2 text-sm">
              <div><span className="font-semibold text-ink">{c.name}</span> <span className="text-muted" dir="ltr">· {c.phone}</span></div>
              <div className="text-muted" dir="ltr">{c.service} · {(c.created_at || "").slice(0, 10)}</div>
            </div>
          ))}
          {!contacts.length && <p className="text-sm text-muted">No requests yet.</p>}
        </div>
      </section>
    </div>
  );
}

/* ---------------- Knowledge Center ---------------- */
function KnowledgeTab({ onLogout }) {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyArticle);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/admin/articles`, authHeaders()); setArticles(r.data); }
    catch (e) { if (e?.response?.status === 401) onLogout(); }
    finally { setLoading(false); }
  }, [onLogout]);
  useEffect(() => { load(); }, [load]);

  const payload = () => ({
    slug: form.slug, service: form.service,
    title: { en: form.title_en, ar: form.title_ar },
    excerpt: { en: form.excerpt_en, ar: form.excerpt_ar },
    body: { en: form.body_en, ar: form.body_ar },
    source_name: form.source_name, source_url: form.source_url,
    official: form.official, published: form.published, date: form.date || undefined,
  });

  const save = async (e) => {
    e.preventDefault(); setErr("");
    if (!form.title_en && !form.title_ar) { setErr("Title is required"); return; }
    setSaving(true);
    try {
      if (form.id) await axios.put(`${API}/admin/articles/${form.id}`, payload(), authHeaders());
      else await axios.post(`${API}/admin/articles`, payload(), authHeaders());
      setForm(emptyArticle); await load();
    } catch (e2) { setErr(e2?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const edit = (a) => setForm({
    id: a.id, slug: a.slug, service: a.service || "",
    title_en: a.title?.en || "", title_ar: a.title?.ar || "",
    excerpt_en: a.excerpt?.en || "", excerpt_ar: a.excerpt?.ar || "",
    body_en: a.body?.en || "", body_ar: a.body?.ar || "",
    source_name: a.source_name || "", source_url: a.source_url || "",
    official: !!a.official, published: !!a.published, date: a.date || "",
  });
  const togglePub = async (a) => { await axios.post(`${API}/admin/articles/${a.id}/publish`, {}, authHeaders()); await load(); };
  const refresh = async (a) => { await axios.post(`${API}/admin/articles/${a.id}/refresh`, {}, authHeaders()); await load(); };
  const del = async (a) => { if (window.confirm("Delete this article?")) { await axios.delete(`${API}/admin/articles/${a.id}`, authHeaders()); await load(); } };
  const [gen, setGen] = useState({ busy: false, msg: "" });
  const generate = async () => {
    setGen({ busy: true, msg: "" });
    try {
      const r = await axios.post(`${API}/admin/knowledge/generate`, {}, authHeaders());
      setGen({ busy: false, msg: r.data.note || `Generated ${r.data.generated}, updated ${r.data.updated}.` });
      await load();
    } catch (e2) { setGen({ busy: false, msg: e2?.response?.data?.detail || "Generation failed (is Smsma AI enabled?)." }); }
  };

  return (
    <div className="space-y-6" data-testid="knowledge-tab">
      <section className="rounded-2xl border border-line bg-white p-6 shadow-card">
        <div className="flex items-center gap-2 mb-5"><BookOpen className="h-4 w-4 text-navy" /><h2 className="font-display text-lg text-navy">{form.id ? "Edit article" : "New article"}</h2></div>
        <form onSubmit={save} className="grid sm:grid-cols-2 gap-3">
          <input placeholder="Slug (auto if empty)" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} data-testid="article-slug" className="h-10 px-3 rounded-lg border border-line" dir="ltr" />
          <input placeholder="Service slug (e.g. vat-registration-uae)" value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} className="h-10 px-3 rounded-lg border border-line" dir="ltr" />
          <input placeholder="Title (English)" value={form.title_en} onChange={(e) => setForm({ ...form, title_en: e.target.value })} data-testid="article-title-en" className="h-10 px-3 rounded-lg border border-line" dir="ltr" />
          <input placeholder="العنوان (عربي)" value={form.title_ar} onChange={(e) => setForm({ ...form, title_ar: e.target.value })} className="h-10 px-3 rounded-lg border border-line" dir="rtl" />
          <input placeholder="Excerpt (English)" value={form.excerpt_en} onChange={(e) => setForm({ ...form, excerpt_en: e.target.value })} className="h-10 px-3 rounded-lg border border-line" dir="ltr" />
          <input placeholder="المقتطف (عربي)" value={form.excerpt_ar} onChange={(e) => setForm({ ...form, excerpt_ar: e.target.value })} className="h-10 px-3 rounded-lg border border-line" dir="rtl" />
          <textarea placeholder="Body (English)" value={form.body_en} onChange={(e) => setForm({ ...form, body_en: e.target.value })} data-testid="article-body-en" className="min-h-[120px] p-3 rounded-lg border border-line sm:col-span-2" dir="ltr" />
          <textarea placeholder="النص (عربي)" value={form.body_ar} onChange={(e) => setForm({ ...form, body_ar: e.target.value })} className="min-h-[120px] p-3 rounded-lg border border-line sm:col-span-2" dir="rtl" />
          <input placeholder="Source name (official)" value={form.source_name} onChange={(e) => setForm({ ...form, source_name: e.target.value })} className="h-10 px-3 rounded-lg border border-line" dir="ltr" />
          <input placeholder="Source URL" value={form.source_url} onChange={(e) => setForm({ ...form, source_url: e.target.value })} className="h-10 px-3 rounded-lg border border-line" dir="ltr" />
          <label className="flex items-center gap-2 text-sm text-ink"><input type="checkbox" checked={form.official} onChange={(e) => setForm({ ...form, official: e.target.checked })} /> Official source</label>
          <label className="flex items-center gap-2 text-sm text-ink"><input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} /> Published</label>
          {err && <p className="text-crimson text-sm sm:col-span-2" data-testid="article-error">{err}</p>}
          <div className="sm:col-span-2 flex gap-3">
            <button disabled={saving} data-testid="article-save" className="h-10 px-6 rounded-full bg-navy text-white font-semibold inline-flex items-center justify-center gap-2 hover:bg-navy-600 transition-colors disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {form.id ? "Update article" : "Create article"}
            </button>
            {form.id && <button type="button" onClick={() => setForm(emptyArticle)} className="h-10 px-6 rounded-full border border-line text-navy font-semibold">Cancel</button>}
          </div>
        </form>
      </section>

      <section className="rounded-2xl border border-line bg-white p-6 shadow-card">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="font-display text-lg text-navy">Articles ({articles.length})</h2>
          <button onClick={generate} disabled={gen.busy} data-testid="generate-guides" className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-crimson text-white font-semibold text-sm hover:bg-crimson-600 transition-colors disabled:opacity-60">
            {gen.busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Generate guides from tax updates
          </button>
        </div>
        {gen.msg && <p className="text-sm text-brandgreen mb-4" data-testid="gen-msg">{gen.msg}</p>}
        <div className="divide-y divide-line">
          {loading && <div className="py-8 grid place-items-center"><Loader2 className="h-5 w-5 animate-spin text-navy" /></div>}
          {!loading && !articles.length && <p className="text-sm text-muted py-2">No articles yet.</p>}
          {articles.map((a) => (
            <div key={a.id} className="py-3 flex items-start justify-between gap-3" data-testid={`admin-article-${a.slug}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[0.6rem] font-bold uppercase px-2 py-0.5 rounded ${a.published ? "bg-brandgreen/10 text-brandgreen" : "bg-muted/10 text-muted"}`}>{a.published ? "Published" : "Draft"}</span>
                  {a.origin && <span className="text-[0.6rem] bg-navy/5 text-navy px-2 py-0.5 rounded">{a.origin}</span>}
                  <span className="text-xs text-muted" dir="ltr">/{a.slug}</span>
                </div>
                <div className="text-ink font-medium mt-1 truncate" dir="ltr">{a.title?.en || a.title?.ar}</div>
                <div className="text-[0.7rem] text-muted mt-0.5" dir="ltr">Updated {(a.updated_at || "").slice(0, 10)}</div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => togglePub(a)} title="Publish/unpublish" data-testid={`toggle-pub-${a.slug}`} className="p-2 rounded-lg text-navy hover:bg-navy/5">{a.published ? <EyeOff className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}</button>
                <button onClick={() => refresh(a)} title="Refresh (bump date)" className="p-2 rounded-lg text-navy hover:bg-navy/5"><RefreshCw className="h-4 w-4" /></button>
                <button onClick={() => edit(a)} title="Edit" data-testid={`edit-${a.slug}`} className="p-2 rounded-lg text-navy hover:bg-navy/5"><FileText className="h-4 w-4" /></button>
                <button onClick={() => del(a)} title="Delete" className="p-2 rounded-lg text-crimson hover:bg-crimson hover:text-white transition-colors"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

/* ---------------- Smsma ---------------- */
function SmsmaTab({ onLogout }) {
  const [cfg, setCfg] = useState(null);
  const [usage, setUsage] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [leads, setLeads] = useState([]);
  const [events, setEvents] = useState([]);
  const [unanswered, setUnanswered] = useState([]);
  const [topics, setTopics] = useState([]);
  const [sources, setSources] = useState([]);
  const [src, setSrc] = useState({ name: "", url: "", kind: "official" });
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    try {
      const [c, u, cv, l, e, un, tp, so] = await Promise.all([
        axios.get(`${API}/admin/smsma/config`, authHeaders()),
        axios.get(`${API}/admin/smsma/usage`, authHeaders()),
        axios.get(`${API}/admin/smsma/conversations`, authHeaders()),
        axios.get(`${API}/admin/smsma/leads`, authHeaders()),
        axios.get(`${API}/admin/smsma/events`, authHeaders()),
        axios.get(`${API}/admin/smsma/unanswered`, authHeaders()),
        axios.get(`${API}/admin/smsma/topics`, authHeaders()),
        axios.get(`${API}/admin/smsma/sources`, authHeaders()),
      ]);
      setCfg(c.data); setUsage(u.data); setConversations(cv.data); setLeads(l.data);
      setEvents(e.data); setUnanswered(un.data); setTopics(tp.data); setSources(so.data);
    } catch (e) { if (e?.response?.status === 401) onLogout(); }
  }, [onLogout]);
  useEffect(() => { load(); }, [load]);

  const saveCfg = async (patch) => {
    setMsg("");
    const r = await axios.put(`${API}/admin/smsma/config`, patch, authHeaders());
    setCfg(r.data); setMsg("Saved."); setTimeout(() => setMsg(""), 2000);
  };
  const addSource = async (e) => {
    e.preventDefault(); if (!src.url) return;
    await axios.post(`${API}/admin/smsma/sources`, src, authHeaders());
    setSrc({ name: "", url: "", kind: "official" }); await load();
  };
  const delSource = async (id) => { await axios.delete(`${API}/admin/smsma/sources/${id}`, authHeaders()); await load(); };

  if (!cfg) return <div className="grid place-items-center py-20"><Loader2 className="h-6 w-6 animate-spin text-navy" /></div>;
  const aiActive = cfg.enabled && !cfg.kill_switch;

  return (
    <div className="space-y-6" data-testid="smsma-tab">
      {/* AI status + controls */}
      <section className="rounded-2xl border border-line bg-white p-6 shadow-card">
        <div className="flex items-center gap-2 mb-4"><Sparkles className="h-4 w-4 text-navy" /><h2 className="font-display text-lg text-navy">Smsma AI status</h2></div>
        <div className={`rounded-xl p-4 mb-5 text-sm ${aiActive ? "bg-brandgreen/10 text-brandgreen" : "bg-gold/10 text-ink"}`} data-testid="ai-status">
          {aiActive ? "AI is ACTIVE — answering visitors with the Emergent Universal Key." : "AI is DISABLED. Visitors are handed off to WhatsApp / Call / Quote. Enable it below when you're ready to activate AI (uses Emergent Universal Key)."}
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          <StatCard icon={MessageSquare} label="Calls today" value={usage?.today?.calls ?? 0} sub={`Limit ${usage?.limits?.daily}/day`} />
          <StatCard icon={TrendingUp} label="Calls this month" value={usage?.month?.calls ?? 0} sub={`Limit ${usage?.limits?.monthly}/mo · ~$${usage?.month?.cost_usd ?? 0}`} />
          <StatCard icon={RefreshCw} label="Cached answers" value={usage?.cache_entries ?? 0} />
        </div>
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <button onClick={() => saveCfg({ enabled: !cfg.enabled })} data-testid="toggle-ai-enabled" className={`h-10 px-5 rounded-full font-semibold inline-flex items-center gap-2 transition-colors ${cfg.enabled ? "bg-brandgreen text-white" : "bg-navy text-white hover:bg-navy-600"}`}>
            <Power className="h-4 w-4" /> {cfg.enabled ? "AI enabled" : "Enable AI"}
          </button>
          <button onClick={() => saveCfg({ kill_switch: !cfg.kill_switch })} data-testid="toggle-kill-switch" className={`h-10 px-5 rounded-full font-semibold inline-flex items-center gap-2 transition-colors ${cfg.kill_switch ? "bg-crimson text-white" : "border border-crimson text-crimson hover:bg-crimson hover:text-white"}`}>
            {cfg.kill_switch ? "Kill switch ON" : "Kill switch"}
          </button>
          {msg && <span className="text-sm text-brandgreen" data-testid="smsma-cfg-msg">{msg}</span>}
        </div>
        <div className="mt-5 grid sm:grid-cols-2 gap-3">
          <label className="text-sm text-muted">WhatsApp number
            <input value={cfg.whatsapp_number || ""} onChange={(e) => setCfg({ ...cfg, whatsapp_number: e.target.value })} onBlur={() => saveCfg({ whatsapp_number: cfg.whatsapp_number })} className="mt-1 w-full h-10 px-3 rounded-lg border border-line" dir="ltr" />
          </label>
          <label className="text-sm text-muted">Default model
            <input value={cfg.ai?.default_model || ""} onChange={(e) => setCfg({ ...cfg, ai: { ...cfg.ai, default_model: e.target.value } })} onBlur={() => saveCfg({ ai: cfg.ai })} className="mt-1 w-full h-10 px-3 rounded-lg border border-line" dir="ltr" />
          </label>
        </div>
      </section>

      <div className="grid lg:grid-cols-2 gap-6">
        <section className="rounded-2xl border border-line bg-white p-6 shadow-card">
          <h2 className="font-display text-lg text-navy mb-4">Unanswered questions ({unanswered.length})</h2>
          <div className="divide-y divide-line max-h-72 overflow-y-auto">
            {unanswered.map((u) => (
              <div key={u.id} className="py-2.5 text-sm"><div className="text-ink">{u.text}</div><div className="text-[0.7rem] text-muted" dir="ltr">{(u.source_page || "")} · {(u.ts || "").slice(0, 10)}</div></div>
            ))}
            {!unanswered.length && <p className="text-sm text-muted">None yet.</p>}
          </div>
        </section>
        <section className="rounded-2xl border border-line bg-white p-6 shadow-card">
          <h2 className="font-display text-lg text-navy mb-4">Frequent topics</h2>
          <div className="flex flex-wrap gap-2">
            {topics.map((t) => (<span key={t.topic} className="text-xs bg-navy/5 text-navy px-3 py-1.5 rounded-full">{t.topic} <b>{t.count}</b></span>))}
            {!topics.length && <p className="text-sm text-muted">No data yet.</p>}
          </div>
        </section>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <section className="rounded-2xl border border-line bg-white p-6 shadow-card">
          <h2 className="font-display text-lg text-navy mb-4">Conversations ({conversations.length})</h2>
          <div className="divide-y divide-line max-h-72 overflow-y-auto">
            {conversations.map((c) => (
              <div key={c.id} className="py-2.5 text-sm flex items-center justify-between gap-2">
                <div className="min-w-0"><div className="text-ink truncate">{c.topic || "(no topic)"}</div><div className="text-[0.7rem] text-muted" dir="ltr">{c.source_page} · {c.messages?.length || 0} msg</div></div>
                <span className="text-[0.6rem] bg-navy/5 text-navy px-2 py-0.5 rounded shrink-0">{c.status}</span>
              </div>
            ))}
            {!conversations.length && <p className="text-sm text-muted">None yet.</p>}
          </div>
        </section>
        <section className="rounded-2xl border border-line bg-white p-6 shadow-card">
          <h2 className="font-display text-lg text-navy mb-4">Leads ({leads.length}) · Handoffs ({events.length})</h2>
          <div className="divide-y divide-line max-h-72 overflow-y-auto">
            {leads.map((l) => (<div key={l.id} className="py-2.5 text-sm"><span className="font-semibold text-ink">{l.name}</span> <span className="text-muted" dir="ltr">· {l.phone}</span><div className="text-[0.7rem] text-muted">{l.topic}</div></div>))}
            {events.map((e) => (<div key={e.id} className="py-2 text-xs text-muted" dir="ltr">↪ {e.type} · {(e.ts || "").slice(0, 10)}</div>))}
            {!leads.length && !events.length && <p className="text-sm text-muted">None yet.</p>}
          </div>
        </section>
      </div>

      <section className="rounded-2xl border border-line bg-white p-6 shadow-card">
        <h2 className="font-display text-lg text-navy mb-4">Trusted knowledge sources</h2>
        <form onSubmit={addSource} className="grid sm:grid-cols-[1fr_1.4fr_auto] gap-3 mb-4">
          <input placeholder="Name" value={src.name} onChange={(e) => setSrc({ ...src, name: e.target.value })} className="h-10 px-3 rounded-lg border border-line" dir="ltr" />
          <input placeholder="https://tax.gov.ae/..." value={src.url} onChange={(e) => setSrc({ ...src, url: e.target.value })} data-testid="source-url" className="h-10 px-3 rounded-lg border border-line" dir="ltr" />
          <button data-testid="add-source" className="h-10 px-5 rounded-full bg-navy text-white font-semibold inline-flex items-center gap-2"><Plus className="h-4 w-4" /> Add</button>
        </form>
        <div className="divide-y divide-line">
          {sources.map((s) => (
            <div key={s.id} className="py-2.5 flex items-center justify-between gap-2 text-sm">
              <div className="min-w-0"><span className="text-ink">{s.name || s.url}</span> {s.trusted ? <span className="text-[0.6rem] text-brandgreen bg-brandgreen/10 px-2 py-0.5 rounded-full ms-1">official</span> : <span className="text-[0.6rem] text-muted bg-muted/10 px-2 py-0.5 rounded-full ms-1">other</span>}<a href={s.url} target="_blank" rel="noopener noreferrer" className="text-navy inline-flex items-center gap-1 ms-2" dir="ltr"><ExternalLink className="h-3 w-3" /></a></div>
              <button onClick={() => delSource(s.id)} className="p-2 rounded-lg text-crimson hover:bg-crimson hover:text-white transition-colors shrink-0"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          {!sources.length && <p className="text-sm text-muted">No sources yet.</p>}
        </div>
      </section>
    </div>
  );
}

/* ---------------- Audit ---------------- */
function AuditTab({ onLogout }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { axios.get(`${API}/admin/audit`, authHeaders()).then((r) => setLogs(r.data)).catch((e) => { if (e?.response?.status === 401) onLogout(); }).finally(() => setLoading(false)); }, [onLogout]);
  return (
    <section className="rounded-2xl border border-line bg-white p-6 shadow-card" data-testid="audit-tab">
      <div className="flex items-center gap-2 mb-4"><ScrollText className="h-4 w-4 text-navy" /><h2 className="font-display text-lg text-navy">Audit trail ({logs.length})</h2></div>
      <div className="divide-y divide-line max-h-[70vh] overflow-y-auto">
        {loading && <div className="py-8 grid place-items-center"><Loader2 className="h-5 w-5 animate-spin text-navy" /></div>}
        {logs.map((l) => (
          <div key={l.id} className="py-2.5 text-sm flex items-center justify-between gap-3" dir="ltr">
            <div><span className="font-semibold text-ink">{l.action}</span> <span className="text-muted">{l.entity}</span> {l.details && <span className="text-muted">· {l.details}</span>}</div>
            <span className="text-[0.7rem] text-muted shrink-0">{(l.ts || "").replace("T", " ").slice(0, 16)}</span>
          </div>
        ))}
        {!loading && !logs.length && <p className="text-sm text-muted">No activity yet.</p>}
      </div>
    </section>
  );
}

/* ---------------- Smart Occasion Engine ---------------- */
const PREVIEW_KEY = "sav_occasion_preview";
const SITE_ORIGIN = window.location.origin;

function Toggle({ on, onClick, label, icon: Icon, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`h-10 px-4 rounded-full font-semibold text-sm inline-flex items-center gap-2 transition-colors ${on ? "bg-brandgreen text-white" : "border border-line text-navy hover:bg-navy/5"}`}>
      {Icon && <Icon className="h-4 w-4" />} {label}: {on ? "ON" : "OFF"}
    </button>
  );
}

function OccasionTab({ onLogout }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  // force override form
  const [force, setForce] = useState({ occasion_id: "", reason: "", expiry: "" });
  // hijri override form
  const [hj, setHj] = useState({ occasion_id: "ramadan", type: "uae_official", start: "", note: "" });
  // preview
  const [previewSel, setPreviewSel] = useState("");
  // AI
  const [aiSel, setAiSel] = useState("uae_union_day");
  const [ai, setAi] = useState({ busy: false, result: null, cached: false, err: "" });

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/occasion/admin`, authHeaders())
      .then((r) => setState(r.data))
      .catch((e) => { if (e?.response?.status === 401) onLogout(); })
      .finally(() => setLoading(false));
  }, [onLogout]);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);
  useEffect(() => { try { setPreviewSel(localStorage.getItem(PREVIEW_KEY) || ""); } catch (e) { /* ignore */ } }, []);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(""), 2500); };

  const saveSettings = async (patch) => {
    setBusy(true);
    try { const r = await axios.put(`${API}/occasion/state`, patch, authHeaders()); setState((s) => ({ ...s, ...r.data })); flash("Saved"); load(); }
    catch (e) { flash("Error saving"); } finally { setBusy(false); }
  };

  const applyForce = async () => {
    if (!force.occasion_id) return flash("Select an occasion to force");
    setBusy(true);
    try { await axios.post(`${API}/occasion/force`, force, authHeaders()); flash("Force override applied (visible to all visitors)"); load(); }
    catch { flash("Error applying override"); } finally { setBusy(false); }
  };
  const cancelForce = async () => {
    setBusy(true);
    try { await axios.delete(`${API}/occasion/force`, authHeaders()); flash("Returned to Automatic"); load(); }
    catch { flash("Error"); } finally { setBusy(false); }
  };

  const applyHijri = async () => {
    if (!hj.start) return flash("Enter the Gregorian start date");
    setBusy(true);
    try { await axios.post(`${API}/occasion/hijri-override`, hj, authHeaders()); flash("Hijri override saved"); load(); }
    catch { flash("Error"); } finally { setBusy(false); }
  };
  const delHijri = async (occasion_id, type) => {
    setBusy(true);
    try { await axios.delete(`${API}/occasion/hijri-override`, { ...authHeaders(), data: { occasion_id, type } }); flash("Override removed"); load(); }
    catch { flash("Error"); } finally { setBusy(false); }
  };

  const runAi = async () => {
    const o = occasionById(aiSel); if (!o) return;
    setAi({ busy: true, result: null, cached: false, err: "" });
    try {
      const r = await axios.post(`${API}/occasion/ai/analyze`, { name: o.name_en, name_ar: o.name_ar, description: `category:${o.category}` }, authHeaders());
      if (r.data.result) setAi({ busy: false, result: r.data.result, cached: r.data.cached, err: "" });
      else setAi({ busy: false, result: null, cached: false, err: r.data.error || "No result" });
    } catch (e) { setAi({ busy: false, result: null, cached: false, err: "AI request failed" }); }
  };

  const setPreview = (id) => {
    try { if (id) localStorage.setItem(PREVIEW_KEY, id); else localStorage.removeItem(PREVIEW_KEY); } catch (e) { /* ignore */ }
    setPreviewSel(id);
  };

  if (loading || !state) return <div className="py-16 grid place-items-center" data-testid="occasion-loading"><Loader2 className="h-6 w-6 animate-spin text-navy" /></div>;

  const uaeDate = state?.uae?.date;
  const hijri = uaeDate ? formatHijri(uaeDate) : null;
  // resolve effective overrides same way backend does for the engine
  const effOverrides = {};
  Object.entries(state.hijri_overrides || {}).forEach(([oid, ov]) => {
    if (ov?.uae_official?.start) effOverrides[oid] = { start: ov.uae_official.start, source: "uae_official_override" };
    else if (ov?.admin_emergency?.start) effOverrides[oid] = { start: ov.admin_emergency.start, source: "admin_override" };
  });
  const forced = state.force && state.force.occasion_id ? occasionById(state.force.occasion_id) : null;
  const detected = uaeDate && !forced ? detectOccasion(uaeDate, effOverrides) : null;
  const active = forced || detected;
  const dateSourceLabel = forced ? "Admin Force Override"
    : active?.dateSource === "uae_official_override" ? "UAE Official Override"
    : active?.dateSource === "admin_override" ? "Admin Emergency Override"
    : active?.dateSource === "gregorian" ? "Calculated (Gregorian)"
    : active?.dateSource === "calculated" ? "Calculated (Hijri)" : "—";

  const box = "rounded-2xl border border-line bg-white p-5 shadow-card";
  const label = "text-xs font-semibold text-muted uppercase tracking-wide";
  const inp = "h-10 px-3 rounded-lg border border-line text-sm w-full";

  return (
    <div className="space-y-5" data-testid="occasion-tab">
      {msg && <div className="rounded-lg bg-navy text-white text-sm px-4 py-2" data-testid="occasion-msg">{msg}</div>}

      {/* Master control + live status */}
      <section className={box}>
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <div className="flex items-center gap-2"><CalendarClock className="h-5 w-5 text-navy" /><h2 className="font-display text-lg text-navy">Smart Occasion Engine</h2></div>
          <button onClick={() => saveSettings({ enabled: !state.enabled })} disabled={busy} data-testid="occasion-master-toggle"
            className={`h-11 px-6 rounded-full font-semibold inline-flex items-center gap-2 transition-colors ${state.enabled ? "bg-brandgreen text-white" : "bg-navy text-white hover:bg-navy-600"}`}>
            <Power className="h-4 w-4" /> Engine {state.enabled ? "ON" : "OFF"}
          </button>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
          <div><div className={label}>Mode</div><div className="font-semibold text-ink mt-1" data-testid="occasion-mode">{forced ? "Manual Force" : state.enabled ? "Automatic" : "Disabled"}</div></div>
          <div><div className={label}>UAE Gregorian</div><div className="font-semibold text-ink mt-1" data-testid="occasion-greg">{uaeDate}</div></div>
          <div><div className={label}>Hijri (calculated)</div><div className="font-semibold text-ink mt-1" data-testid="occasion-hijri">{hijri?.text}</div></div>
          <div><div className={label}>UAE Time</div><div className="font-semibold text-ink mt-1" data-testid="occasion-time">{state?.uae?.time} · {state?.uae?.timezone}</div></div>
          <div><div className={label}>Detected Occasion</div><div className="font-semibold text-ink mt-1" data-testid="occasion-detected">{active ? active.name_en : "None today"}</div></div>
          <div><div className={label}>Category</div><div className="font-semibold text-ink mt-1">{active?.category || "—"}</div></div>
          <div><div className={label}>Priority</div><div className="font-semibold text-ink mt-1">{active?.priority ?? "—"}</div></div>
          <div><div className={label}>Active Experience</div><div className="font-semibold text-ink mt-1">{active?.experience || "—"}</div></div>
          <div className="lg:col-span-2"><div className={label}>Date Source</div><div className="font-semibold text-ink mt-1" data-testid="occasion-source">{dateSourceLabel}</div></div>
          <div className="lg:col-span-2"><div className={label}>Registry size</div><div className="font-semibold text-ink mt-1">{OCCASION_COUNT} occasions ({OCCASIONS.filter((o) => o.active).length} active)</div></div>
        </div>
      </section>

      {/* Display settings */}
      <section className={box}>
        <div className="flex items-center gap-2 mb-4"><Gauge className="h-4 w-4 text-navy" /><h3 className="font-display text-navy">Display settings</h3></div>
        <div className="flex flex-wrap gap-3 items-center">
          <Toggle on={state.desktop !== false} onClick={() => saveSettings({ desktop: !(state.desktop !== false) })} label="Desktop" icon={Monitor} testid="occasion-desktop-toggle" />
          <Toggle on={state.mobile !== false} onClick={() => saveSettings({ mobile: !(state.mobile !== false) })} label="Mobile" icon={Smartphone} testid="occasion-mobile-toggle" />
          <Toggle on={state.cinematic_intro !== false} onClick={() => saveSettings({ cinematic_intro: !(state.cinematic_intro !== false) })} label="Cinematic Intro" icon={Film} testid="occasion-intro-toggle" />
          <div className="inline-flex items-center gap-2">
            <span className="text-sm text-muted">Intensity</span>
            <select value={state.intensity || "full"} onChange={(e) => saveSettings({ intensity: e.target.value })} data-testid="occasion-intensity" className="h-10 px-3 rounded-lg border border-line text-sm">
              <option value="full">Full</option><option value="medium">Medium</option><option value="subtle">Subtle</option>
            </select>
          </div>
        </div>
      </section>

      {/* Admin-only preview */}
      <section className={box}>
        <div className="flex items-center gap-2 mb-2"><Eye className="h-4 w-4 text-navy" /><h3 className="font-display text-navy">Preview (admin-only)</h3></div>
        <p className="text-xs text-muted mb-3">Preview renders only in your authenticated browser. Public visitors are never affected.</p>
        <div className="flex flex-wrap gap-3 items-center">
          <select value={previewSel} onChange={(e) => setPreview(e.target.value)} data-testid="occasion-preview-select" className="h-10 px-3 rounded-lg border border-line text-sm min-w-[220px]">
            <option value="">— No preview —</option>
            {OCCASIONS.map((o) => <option key={o.id} value={o.id}>{o.name_en}</option>)}
          </select>
          <a href={`${SITE_ORIGIN}/en?occasion=${previewSel}`} target="_blank" rel="noreferrer"
            className={`h-10 px-5 rounded-full font-semibold text-sm inline-flex items-center gap-2 ${previewSel ? "bg-navy text-white hover:bg-navy-600" : "pointer-events-none opacity-40 bg-navy text-white"}`}
            data-testid="occasion-preview-open"><ExternalLink className="h-4 w-4" /> Open site preview</a>
          <button onClick={() => setPreview("")} data-testid="occasion-preview-clear" className="h-10 px-5 rounded-full border border-line text-navy font-semibold text-sm">Clear preview</button>
        </div>
      </section>

      {/* Force / Override */}
      <section className={box}>
        <div className="flex items-center gap-2 mb-2"><ShieldAlert className="h-4 w-4 text-crimson" /><h3 className="font-display text-navy">Force / Emergency Override</h3></div>
        <p className="text-xs text-muted mb-3">Server-side override visible to ALL visitors. Auto-stops at expiry. Distinct from the UAE Official Hijri Override below.</p>
        {state.force && state.force.occasion_id ? (
          <div className="rounded-xl border border-crimson/30 bg-crimson/5 p-4 mb-3 text-sm" data-testid="occasion-force-active">
            <div className="font-semibold text-crimson mb-1">Active override: {forced?.name_en || state.force.occasion_id}</div>
            <div className="text-muted">By {state.force.admin} · start {state.force.start} · expiry {state.force.expiry || "none"}{state.force.reason ? ` · reason: ${state.force.reason}` : ""}</div>
            <button onClick={cancelForce} disabled={busy} data-testid="occasion-force-cancel" className="mt-3 h-9 px-5 rounded-full bg-crimson text-white font-semibold text-sm inline-flex items-center gap-2"><Ban className="h-4 w-4" /> Cancel — Return to Automatic</button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-4 gap-3 items-end">
            <div><div className={label}>Occasion</div><select value={force.occasion_id} onChange={(e) => setForce({ ...force, occasion_id: e.target.value })} data-testid="occasion-force-select" className={inp}><option value="">Select…</option>{OCCASIONS.map((o) => <option key={o.id} value={o.id}>{o.name_en}</option>)}</select></div>
            <div><div className={label}>Reason</div><input value={force.reason} onChange={(e) => setForce({ ...force, reason: e.target.value })} data-testid="occasion-force-reason" className={inp} placeholder="e.g. official announcement" /></div>
            <div><div className={label}>Expiry (UAE date)</div><input type="date" value={force.expiry} onChange={(e) => setForce({ ...force, expiry: e.target.value })} data-testid="occasion-force-expiry" className={inp} /></div>
            <button onClick={applyForce} disabled={busy} data-testid="occasion-force-apply" className="h-10 px-5 rounded-full bg-navy text-white font-semibold text-sm">Apply override</button>
          </div>
        )}
      </section>

      {/* UAE Official / Admin Emergency Hijri overrides */}
      <section className={box}>
        <div className="flex items-center gap-2 mb-2"><CalendarClock className="h-4 w-4 text-navy" /><h3 className="font-display text-navy">Hijri Date Overrides</h3></div>
        <p className="text-xs text-muted mb-3">Precedence: UAE Official Override → Admin Emergency Override → calculated fallback. AI never sets these dates.</p>
        <div className="grid sm:grid-cols-5 gap-3 items-end">
          <div><div className={label}>Occasion</div><select value={hj.occasion_id} onChange={(e) => setHj({ ...hj, occasion_id: e.target.value })} data-testid="occasion-hijri-occ" className={inp}>{OCCASIONS.filter((o) => o.calendar === "hijri").map((o) => <option key={o.id} value={o.id}>{o.name_en}</option>)}</select></div>
          <div><div className={label}>Type</div><select value={hj.type} onChange={(e) => setHj({ ...hj, type: e.target.value })} data-testid="occasion-hijri-type" className={inp}><option value="uae_official">UAE Official</option><option value="admin_emergency">Admin Emergency</option></select></div>
          <div><div className={label}>Gregorian start</div><input type="date" value={hj.start} onChange={(e) => setHj({ ...hj, start: e.target.value })} data-testid="occasion-hijri-start" className={inp} /></div>
          <div><div className={label}>Note</div><input value={hj.note} onChange={(e) => setHj({ ...hj, note: e.target.value })} data-testid="occasion-hijri-note" className={inp} placeholder="source / note" /></div>
          <button onClick={applyHijri} disabled={busy} data-testid="occasion-hijri-apply" className="h-10 px-5 rounded-full bg-navy text-white font-semibold text-sm">Save override</button>
        </div>
        <div className="mt-4 space-y-2" data-testid="occasion-hijri-list">
          {Object.entries(state.hijri_overrides || {}).map(([oid, ov]) => (
            <div key={oid} className="text-sm border-t border-line pt-2">
              <span className="font-semibold text-ink">{occasionById(oid)?.name_en || oid}</span>
              {["uae_official", "admin_emergency"].map((t) => ov?.[t] ? (
                <span key={t} className="ml-3 inline-flex items-center gap-2 text-muted">
                  {t === "uae_official" ? "Official" : "Emergency"}: {ov[t].start}
                  <button onClick={() => delHijri(oid, t)} data-testid={`occasion-hijri-del-${oid}-${t}`} className="text-crimson"><Trash2 className="h-3.5 w-3.5" /></button>
                </span>
              ) : null)}
            </div>
          ))}
          {!Object.keys(state.hijri_overrides || {}).length && <p className="text-sm text-muted">No overrides — using calculated Hijri dates.</p>}
        </div>
      </section>

      {/* AI Occasion Intelligence (admin-only) */}
      <section className={box}>
        <div className="flex items-center gap-2 mb-2"><Wand2 className="h-4 w-4 text-gold" /><h3 className="font-display text-navy">AI Occasion Intelligence <span className="text-xs text-muted font-normal">(Gemini 3 Flash · admin-only · cached)</span></h3></div>
        <p className="text-xs text-muted mb-3">Advisory only — classification, priority & visual-language recommendations. AI never sets dates or activates occasions. Zero calls on public traffic.</p>
        <div className="flex flex-wrap gap-3 items-center">
          <select value={aiSel} onChange={(e) => setAiSel(e.target.value)} data-testid="occasion-ai-select" className="h-10 px-3 rounded-lg border border-line text-sm min-w-[220px]">{OCCASIONS.map((o) => <option key={o.id} value={o.id}>{o.name_en}</option>)}</select>
          <button onClick={runAi} disabled={ai.busy} data-testid="occasion-ai-run" className="h-10 px-5 rounded-full bg-gold text-ink font-semibold text-sm inline-flex items-center gap-2">{ai.busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Analyze</button>
        </div>
        {ai.err && <p className="mt-3 text-sm text-crimson" data-testid="occasion-ai-err">{ai.err}</p>}
        {ai.result && (
          <div className="mt-4 rounded-xl bg-navy/5 p-4 text-sm grid sm:grid-cols-2 gap-2" data-testid="occasion-ai-result">
            <div><span className={label}>Category</span><div className="font-semibold">{ai.result.category}</div></div>
            <div><span className={label}>Classification</span><div className="font-semibold">{ai.result.classification}</div></div>
            <div><span className={label}>Tone</span><div className="font-semibold">{ai.result.tone}</div></div>
            <div><span className={label}>Priority / Level</span><div className="font-semibold">{ai.result.priority} / {ai.result.level}</div></div>
            <div className="sm:col-span-2"><span className={label}>Visual language</span><div className="font-semibold">{ai.result.visual_language}</div></div>
            <div className="sm:col-span-2"><span className={label}>Respectful treatment</span><div className="font-semibold">{ai.result.respectful ? "Yes (restrained)" : "No (celebratory)"}</div></div>
            {ai.result.rationale && <div className="sm:col-span-2 text-muted">{ai.result.rationale}</div>}
            <div className="sm:col-span-2 text-[0.7rem] text-muted">{ai.cached ? "Loaded from cache (no credits used)." : "Fresh AI result — cached for reuse."}</div>
          </div>
        )}
      </section>

      {/* Audit */}
      <section className={box}>
        <div className="flex items-center gap-2 mb-3"><ScrollText className="h-4 w-4 text-navy" /><h3 className="font-display text-navy">Override audit ({(state.audit || []).length})</h3></div>
        <div className="divide-y divide-line max-h-72 overflow-y-auto" data-testid="occasion-audit">
          {(state.audit || []).map((l) => (
            <div key={l.id} className="py-2 text-sm flex items-center justify-between gap-3" dir="ltr">
              <div><span className="font-semibold text-ink">{l.action}</span> <span className="text-muted">by {l.admin}</span> {l.details && <span className="text-muted">· {JSON.stringify(l.details).slice(0, 80)}</span>}</div>
              <span className="text-[0.7rem] text-muted shrink-0">{(l.at || "").replace("T", " ").slice(0, 16)}</span>
            </div>
          ))}
          {!(state.audit || []).length && <p className="text-sm text-muted">No override activity yet.</p>}
        </div>
      </section>
    </div>
  );
}

const TABS = [
  { key: "overview", label: "Overview", icon: TrendingUp },
  { key: "knowledge", label: "Knowledge Center", icon: BookOpen },
  { key: "legal", label: "Legal Registry", icon: Scale },
  { key: "smsma", label: "Smsma", icon: Sparkles },
  { key: "occasions", label: "Occasions", icon: CalendarClock },
  { key: "audit", label: "Audit", icon: ScrollText },
];

const STATUS_COLORS = {
  ACTIVE: "bg-brandgreen/10 text-brandgreen", AMENDED: "bg-gold/20 text-ink",
  SUPERSEDED: "bg-muted/15 text-muted", HISTORICAL: "bg-muted/15 text-muted",
  PENDING_REVIEW: "bg-crimson-soft text-crimson-600", REPEALED: "bg-muted/15 text-muted",
};
const emptyLegal = { type: "Cabinet Decision", number: "", year: "", title_en: "", title_ar: "", topic: "", effective: "", status: "ACTIVE", source_url: "", interpretation: "" };

function LegalTab({ onLogout }) {
  const [items, setItems] = useState([]);
  const [topics, setTopics] = useState([]);
  const [topic, setTopic] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyLegal);
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (topic) params.set("topic", topic);
      if (q) params.set("q", q);
      const r = await axios.get(`${API}/admin/legal?${params}`, authHeaders());
      setItems(r.data.items); setTopics(r.data.topics);
    } catch (e) { if (e?.response?.status === 401) onLogout(); }
    finally { setLoading(false); }
  }, [onLogout, topic, q]);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault(); setMsg("");
    if (!form.number || !form.year || !form.title_en) { setMsg("Type, number, year and English title are required."); return; }
    try {
      await axios.post(`${API}/admin/legal`, { ...form, year: parseInt(form.year), topic: form.topic ? form.topic.split(",").map((s) => s.trim()) : [] }, authHeaders());
      setForm(emptyLegal); setMsg("Saved."); await load(); setTimeout(() => setMsg(""), 2000);
    } catch (e2) { setMsg(e2?.response?.data?.detail || "Save failed"); }
  };
  const scan = async () => { const r = await axios.post(`${API}/admin/legal/scan`, {}, authHeaders()); setMsg(r.data.note); await load(); };
  const del = async (id) => { if (window.confirm("Delete this authority?")) { await axios.delete(`${API}/admin/legal/${id}`, authHeaders()); await load(); } };

  return (
    <div className="space-y-6" data-testid="legal-tab">
      <section className="rounded-2xl border border-line bg-white p-6 shadow-card">
        <div className="flex items-center gap-2 mb-4"><Scale className="h-4 w-4 text-navy" /><h2 className="font-display text-lg text-navy">Add / update legal authority</h2></div>
        <form onSubmit={save} className="grid sm:grid-cols-2 gap-3">
          <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="legal-type" className="h-10 px-3 rounded-lg border border-line" dir="ltr">
            {["Federal Decree-Law", "Federal Law", "Cabinet Decision", "Ministerial Decision", "FTA Decision", "Executive Regulation", "Public Clarification", "Official Guide"].map((t) => <option key={t}>{t}</option>)}
          </select>
          <div className="grid grid-cols-2 gap-3">
            <input placeholder="Number" value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} data-testid="legal-number" className="h-10 px-3 rounded-lg border border-line" dir="ltr" />
            <input placeholder="Year" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} data-testid="legal-year" className="h-10 px-3 rounded-lg border border-line" dir="ltr" />
          </div>
          <input placeholder="Title (English)" value={form.title_en} onChange={(e) => setForm({ ...form, title_en: e.target.value })} data-testid="legal-title-en" className="h-10 px-3 rounded-lg border border-line" dir="ltr" />
          <input placeholder="العنوان (عربي)" value={form.title_ar} onChange={(e) => setForm({ ...form, title_ar: e.target.value })} className="h-10 px-3 rounded-lg border border-line" dir="rtl" />
          <input placeholder="Topics (comma separated)" value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} className="h-10 px-3 rounded-lg border border-line" dir="ltr" />
          <input placeholder="Effective date / period" value={form.effective} onChange={(e) => setForm({ ...form, effective: e.target.value })} className="h-10 px-3 rounded-lg border border-line" dir="ltr" />
          <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="h-10 px-3 rounded-lg border border-line" dir="ltr">
            {["ACTIVE", "AMENDED", "SUPERSEDED", "HISTORICAL", "REPEALED", "PENDING_REVIEW"].map((s) => <option key={s}>{s}</option>)}
          </select>
          <input placeholder="Official source URL" value={form.source_url} onChange={(e) => setForm({ ...form, source_url: e.target.value })} className="h-10 px-3 rounded-lg border border-line" dir="ltr" />
          <textarea placeholder="Verified interpretation / note" value={form.interpretation} onChange={(e) => setForm({ ...form, interpretation: e.target.value })} className="min-h-[70px] p-3 rounded-lg border border-line sm:col-span-2" dir="ltr" />
          {msg && <p className="text-sm text-brandgreen sm:col-span-2" data-testid="legal-msg">{msg}</p>}
          <div className="sm:col-span-2 flex gap-3">
            <button data-testid="legal-save" className="h-10 px-6 rounded-full bg-navy text-white font-semibold inline-flex items-center gap-2 hover:bg-navy-600 transition-colors"><Save className="h-4 w-4" /> Save authority</button>
            <button type="button" onClick={scan} data-testid="legal-scan" className="h-10 px-6 rounded-full border border-line text-navy font-semibold inline-flex items-center gap-2"><RefreshCw className="h-4 w-4" /> Scan tax updates</button>
          </div>
        </form>
      </section>

      <section className="rounded-2xl border border-line bg-white p-6 shadow-card">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="font-display text-lg text-navy">Legal authorities ({items.length})</h2>
          <div className="flex flex-wrap gap-2 items-center">
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="legal-search" placeholder="Search…" className="h-9 px-3 rounded-full border border-line text-sm" dir="ltr" />
            <select value={topic} onChange={(e) => setTopic(e.target.value)} data-testid="legal-topic-filter" className="h-9 px-3 rounded-full border border-line text-sm" dir="ltr">
              <option value="">All topics</option>
              {topics.map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
        </div>
        <div className="divide-y divide-line">
          {loading && <div className="py-8 grid place-items-center"><Loader2 className="h-5 w-5 animate-spin text-navy" /></div>}
          {!loading && !items.length && <p className="text-sm text-muted py-2">No authorities found.</p>}
          {items.map((a) => (
            <div key={a.id} className="py-3 flex items-start justify-between gap-3" data-testid="legal-row">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[0.6rem] font-bold uppercase px-2 py-0.5 rounded ${STATUS_COLORS[a.status] || "bg-muted/15 text-muted"}`}>{a.status}</span>
                  <span className="font-semibold text-ink" dir="ltr">{a.type} No. {a.number} of {a.year}</span>
                  {(a.topic || []).map((t) => <span key={t} className="text-[0.6rem] bg-navy/5 text-navy px-2 py-0.5 rounded">{t}</span>)}
                </div>
                <div className="text-sm text-ink/80 mt-1" dir="ltr">{a.title_en}</div>
                {a.effective && <div className="text-[0.7rem] text-muted mt-0.5" dir="ltr">Effective: {a.effective}{a.superseded_by ? ` · superseded by ${a.superseded_by}` : ""}{a.amended_by ? ` · amended by ${a.amended_by}` : ""}</div>}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {a.source_url && <a href={a.source_url} target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg text-navy hover:bg-navy/5"><ExternalLink className="h-4 w-4" /></a>}
                <button onClick={() => del(a.id)} data-testid="legal-delete" className="p-2 rounded-lg text-crimson hover:bg-crimson hover:text-white transition-colors"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const [tab, setTab] = useState("overview");
  return (
    <div className="min-h-screen bg-surface">
      <header className="bg-navy text-white">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 font-display text-lg"><ShieldCheck className="h-5 w-5 text-crimson-soft" /> SAV Admin</div>
          <button onClick={onLogout} data-testid="admin-logout" className="inline-flex items-center gap-2 text-sm font-semibold bg-white/10 hover:bg-white/20 px-4 h-9 rounded-full transition-colors"><LogOut className="h-4 w-4" /> Sign out</button>
        </div>
        <div className="max-w-6xl mx-auto px-5 flex gap-1 overflow-x-auto">
          {TABS.map((tb) => (
            <button key={tb.key} onClick={() => setTab(tb.key)} data-testid={`tab-${tb.key}`}
              className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 whitespace-nowrap transition-colors ${tab === tb.key ? "border-gold text-white" : "border-transparent text-white/60 hover:text-white"}`}>
              <tb.icon className="h-4 w-4" /> {tb.label}
            </button>
          ))}
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-5 py-8">
        {tab === "overview" && <OverviewTab onLogout={onLogout} />}
        {tab === "knowledge" && <KnowledgeTab onLogout={onLogout} />}
        {tab === "legal" && <LegalTab onLogout={onLogout} />}
        {tab === "smsma" && <SmsmaTab onLogout={onLogout} />}
        {tab === "occasions" && <OccasionTab onLogout={onLogout} />}
        {tab === "audit" && <AuditTab onLogout={onLogout} />}
      </main>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(!!localStorage.getItem(TOKEN_KEY));
  useEffect(() => {
    if (localStorage.getItem(TOKEN_KEY)) {
      axios.get(`${API}/admin/me`, authHeaders()).then(() => setAuthed(true)).catch(() => { localStorage.removeItem(TOKEN_KEY); setAuthed(false); });
    }
  }, []);
  const logout = () => { localStorage.removeItem(TOKEN_KEY); setAuthed(false); };
  return authed ? <Dashboard onLogout={logout} /> : <Login onLogin={() => setAuthed(true)} />;
}
